// externals
import type { BaggageEntry } from '@makemydeal/dr-platform-shared';
import { getBaggageEntryByName } from '@makemydeal/dr-platform-shared';

// libraries
import type { DealerReviewBaggageContent } from '@makemydeal/dr-shared-types';
import { DXG_DEALER_REVIEW_BAGGAGE_NAME, isSalesCheckInName } from '@makemydeal/dr-shared-types';

const isNonEmptyString = (value: unknown): value is string => typeof value === 'string' && value.length > 0;

// Deliberately does not inspect `value.type`: the envelope declares it required, but the payload the
// writer actually sends omits it, so requiring it would reject every valid marker.
const isDealerReviewContent = (content: unknown): content is DealerReviewBaggageContent => {
    if (typeof content !== 'object' || content === null) {
        return false;
    }

    const candidate = content as Record<string, unknown>;

    return (
        isNonEmptyString(candidate.dealXgVersion) &&
        isNonEmptyString(candidate.source) &&
        Array.isArray(candidate.changedFields) &&
        candidate.changedFields.every((field) => typeof field === 'string')
    );
};

// `changedAt` is not required: rejecting over a missing timestamp would lose the changed-field list
// that drives the branch.
export const readDealerReviewMarker = (baggage: BaggageEntry[] | undefined): DealerReviewBaggageContent | undefined => {
    const entry = getBaggageEntryByName<{ content?: unknown }>(baggage, DXG_DEALER_REVIEW_BAGGAGE_NAME);
    const content = entry?.value?.content;

    return isDealerReviewContent(content) ? content : undefined;
};

export const isSalesCheckInMarker = (marker: DealerReviewBaggageContent | undefined): boolean => isSalesCheckInName(marker?.source);

export const readUsableDealerReviewMarker = (baggage: BaggageEntry[] | undefined): DealerReviewBaggageContent | undefined => {
    const marker = readDealerReviewMarker(baggage);

    return isSalesCheckInMarker(marker) ? marker : undefined;
};
